import * as React from "react";
import { LifeBuoy, X, Send, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useSupportThread, useSendSupportMessage } from "@/hooks/useSupport";
import { createSupportConnection } from "@/lib/supportHub";
import { ApiError } from "@/lib/api";
import type { SupportChatMessage } from "@/types/api";

/**
 * Live support chat for customers. Loads the existing conversation, opens a
 * SignalR connection, and streams operator replies in real time. Sending posts
 * the message (which also emails the team). The button gently bobs and shows an
 * unread dot when a reply arrives while the panel is closed.
 */
export function SupportWidget() {
  const [open, setOpen] = React.useState(false);
  const [input, setInput] = React.useState("");
  const [messages, setMessages] = React.useState<SupportChatMessage[]>([]);
  const [unread, setUnread] = React.useState(false);

  const { data: thread } = useSupportThread();
  const send = useSendSupportMessage();
  const { toast } = useToast();
  const scrollRef = React.useRef<HTMLDivElement>(null);

  // Seed from the loaded thread.
  React.useEffect(() => {
    if (thread?.messages) setMessages(thread.messages);
  }, [thread]);

  // Live connection — append operator replies as they arrive.
  React.useEffect(() => {
    let disposed = false;
    const conn = createSupportConnection();
    conn.on("replyReceived", (payload: { message: SupportChatMessage }) => {
      if (!payload?.message) return;
      setMessages((prev) =>
        prev.some((m) => m.id === payload.message.id) ? prev : [...prev, payload.message]
      );
      setUnread((u) => (open ? u : true));
    });
    // Defer one tick so React StrictMode's immediate unmount cancels the start
    // before negotiation begins (avoids the benign "stopped during negotiation").
    const t = window.setTimeout(() => {
      if (!disposed) conn.start().catch(() => { /* hub unavailable — reload still works */ });
    }, 0);
    return () => { disposed = true; window.clearTimeout(t); conn.stop().catch(() => {}); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-scroll + clear unread when open.
  React.useEffect(() => {
    if (open) {
      setUnread(false);
      requestAnimationFrame(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
      });
    }
  }, [open, messages]);

  async function submit() {
    const body = input.trim();
    if (!body || send.isPending) return;
    try {
      const res = await send.mutateAsync(body);
      setMessages((prev) => [...prev, res.message]);
      setInput("");
    } catch (err) {
      toast({
        title: "Couldn't send",
        description: err instanceof ApiError ? err.message : "Please try again.",
        variant: "destructive"
      });
    }
  }

  return (
    <>
      <style>{`
        @keyframes hd-support-bob { from { transform: translateY(0); } to { transform: translateY(-6px); } }
        .hd-support-bob { animation: hd-support-bob 1.6s ease-in-out infinite alternate; }
      `}</style>

      {open && (
        <div className="fixed bottom-24 right-5 z-50 flex h-[460px] w-[min(370px,calc(100vw-2.5rem))] flex-col rounded-2xl border border-border bg-background shadow-brand-lg">
          <div className="flex items-center justify-between rounded-t-2xl bg-primary px-4 py-3 text-primary-foreground">
            <span className="flex items-center gap-2 font-semibold"><LifeBuoy className="h-4 w-4" /> Support</span>
            <button onClick={() => setOpen(false)} aria-label="Close" className="rounded p-1 hover:bg-white/15">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 space-y-2.5 overflow-y-auto p-4">
            {messages.length === 0 ? (
              <p className="mt-6 text-center text-sm text-muted-foreground">
                Send us a message and we'll reply here and by email.
              </p>
            ) : (
              messages.map((m) => (
                <div key={m.id} className={`flex ${m.sender_type === "customer" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${
                      m.sender_type === "customer"
                        ? "rounded-br-sm bg-primary text-primary-foreground"
                        : "rounded-bl-sm bg-muted text-foreground"
                    }`}
                  >
                    {m.sender_type === "operator" && (
                      <p className="mb-0.5 text-[10px] font-semibold uppercase tracking-wider opacity-70">
                        {m.sender_name || "Support"}
                      </p>
                    )}
                    <p className="whitespace-pre-wrap">{m.body}</p>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="flex items-end gap-2 border-t border-border p-3">
            <textarea
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void submit(); } }}
              placeholder="Type a message…"
              className="max-h-24 flex-1 resize-none rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            <Button size="icon" onClick={submit} disabled={send.isPending || !input.trim()} aria-label="Send">
              {send.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      )}

      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="Support"
        className="fixed bottom-5 right-5 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-brand-lg transition-transform hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        <span className={open ? "" : "hd-support-bob"}>
          {open ? <X className="h-6 w-6" /> : <LifeBuoy className="h-6 w-6" />}
        </span>
        {unread && !open && (
          <span className="absolute right-1 top-1 h-3 w-3 rounded-full border-2 border-primary bg-red-500" />
        )}
      </button>
    </>
  );
}
